/**
 * Utilidades para convertir entre el formato de UI y el esquema de la base de datos
 * para las mediciones de calibración
 */

// ═══════════════════════════════════════════════════════════════
// CONVERSIÓN: UI → ESQUEMA DB (Para guardar)
// ═══════════════════════════════════════════════════════════════

/**
 * Convierte datos de patrones del formato UI al esquema de DB
 * @param {Object} datosPatrones - { patronIdx: { magnitud: { puntoCalibracion: [], mediciones: [][], ... } } }
 * @param {Object} magnitudConfig - Configuración de la magnitud
 * @param {Array} puntosCalibracion - Array con los valores de los puntos [1.5, 2.0, 3.5]
 * @returns {Array} medicionesPatrones según esquema DB
 */
export const convertirPatronesAEsquema = (datosPatrones, magnitudNombre, magnitudConfig, puntosCalibracion, patronesReales) => {
  const medicionesPatrones = [];
  
  // Normalizar nombres de propiedades
  const requiereHisteresis = magnitudConfig.histeresis ?? magnitudConfig.requiereHisteresis;
  const histeresisEnTodos = magnitudConfig.histeresisEnTodosLosPuntos;
  
  Object.keys(datosPatrones).forEach(patronIdx => {
    const datosPatron = datosPatrones[patronIdx][magnitudNombre];
    if (!datosPatron) return;

    const patronReal = patronesReales[patronIdx];
    const patronId = patronReal?._id || patronReal?.id;

    // Por cada punto de calibración, crear un objeto medicionPatronSchema
    puntosCalibracion.forEach((puntoCal, puntoIdx) => {
      const medicionObj = {
        patron: patronId,
        puntoCal: puntoCal || 0
      };

      // Si requiere histéresis en todos los puntos
      if (requiereHisteresis && histeresisEnTodos) {
        medicionObj.medicionesSubida = datosPatron.medicionesSubida?.[puntoIdx] || [];
        medicionObj.medicionesBajada = datosPatron.medicionesBajada?.[puntoIdx] || [];
      } else {
        // Mediciones normales (sin histéresis)
        medicionObj.mediciones = datosPatron.mediciones?.[puntoIdx] || [];
      }

      medicionesPatrones.push(medicionObj);
    });

    // Si hay histéresis en un solo punto (puntoHisteresis) — marcar flag
    // `histeresis: true` para que el endpoint `/EqInOt/certificado/` lo
    // identifique sin depender de la posición dentro del array.
    if (requiereHisteresis && !histeresisEnTodos && datosPatron.puntoHisteresis) {
      const medicionHist = {
        patron: patronId,
        puntoCal: datosPatron.puntoHisteresis.puntoCal || 0,
        mediciones: datosPatron.puntoHisteresis.mediciones || [],
        histeresis: true
      };
      medicionesPatrones.push(medicionHist);
    }
  });

  return medicionesPatrones;
};

/**
 * Convierte datos de IBC del formato UI al esquema de DB
 * @param {Object} datosIBC - { equipoId: { magnitud: { puntoCalibracion: [], mediciones: [][], ... } } }
 * @param {Object} magnitudConfig - Configuración de la magnitud
 * @param {Array} puntosCalibracion - Array con los valores de los puntos
 * @returns {Array} medicionesIBC según esquema DB
 */
export const convertirIBCAEsquema = (datosIBC, magnitudConfig, puntosCalibracion) => {
  const medicionesIBC = [];

  // Por cada punto de calibración, crear un objeto medicionIBCSchema
  puntosCalibracion.forEach((puntoCal, puntoIdx) => {
    const medicionObj = {
      puntoCal: puntoCal || 0
    };

    // Si requiere histéresis en todos los puntos
    if (magnitudConfig.requiereHisteresis && magnitudConfig.histeresisEnTodosLosPuntos) {
      medicionObj.medicionesSubida = datosIBC.medicionesSubida?.[puntoIdx] || [];
      medicionObj.medicionesBajada = datosIBC.medicionesBajada?.[puntoIdx] || [];
    } else {
      // Mediciones normales (sin histéresis)
      medicionObj.mediciones = datosIBC.mediciones?.[puntoIdx] || [];
    }

    medicionesIBC.push(medicionObj);
  });

  // Si hay histéresis en un solo punto (puntoHisteresis)
  if (magnitudConfig.requiereHisteresis && !magnitudConfig.histeresisEnTodosLosPuntos && datosIBC.puntoHisteresis) {
    const medicionHist = {
      puntoCal: datosIBC.puntoHisteresis.puntoCal || 0,
      mediciones: datosIBC.puntoHisteresis.mediciones || [],
      // Marcar explícitamente como punto de histéresis — permite identificarlo
      // sin depender de la posición (idx >= cantPuntos), y evita que el filtro
      // sparse del backend lo omita cuando llega solo con `puntoCal` (sin mediciones).
      histeresis: true
    };
    medicionesIBC.push(medicionHist);
  }

  return medicionesIBC;
};

// ═══════════════════════════════════════════════════════════════
// CONVERSIÓN: ESQUEMA DB → UI (Para cargar datos existentes)
// ═══════════════════════════════════════════════════════════════

/**
 * Convierte medicionesPatrones del esquema DB al formato UI
 * @param {Array} medicionesPatrones - Array según medicionPatronSchema
 * @param {Object} magnitudConfig - Configuración de la magnitud
 * @param {Number} patronIdx - Índice del patrón
 * @returns {Object} Estructura para UI
 */
export const convertirEsquemaAPatrones = (medicionesPatrones, magnitudConfig, patronIdx) => {
  // Normalizar nombres de propiedades
  const requiereHisteresis = magnitudConfig.histeresis ?? magnitudConfig.requiereHisteresis;
  const histeresisEnTodos = magnitudConfig.histeresisEnTodosLosPuntos;
  // Compat de nombres: plantilla usa `cantPuntos`, lote usa `cantidadPuntos`.
  const cantPuntos = magnitudConfig.cantidadPuntos ?? magnitudConfig.cantPuntos ?? 0;
  const cantMediciones = magnitudConfig.cantMedicionesPorPunto;

  if (!medicionesPatrones || medicionesPatrones.length === 0) {
    return {
      puntoCalibracion: Array(cantPuntos).fill(''),
      mediciones: Array(cantPuntos).fill(null).map(() => 
        Array(cantMediciones).fill('')
      ),
      medicionesSubida: requiereHisteresis && histeresisEnTodos 
        ? Array(cantPuntos).fill(null).map(() => Array(cantMediciones).fill(''))
        : undefined,
      medicionesBajada: requiereHisteresis && histeresisEnTodos
        ? Array(cantPuntos).fill(null).map(() => Array(cantMediciones).fill(''))
        : undefined,
      puntoHisteresis: requiereHisteresis && !histeresisEnTodos
        ? { puntoCal: '', mediciones: Array(cantMediciones).fill('') }
        : undefined
    };
  }

  const estructura = {
    puntoCalibracion: [],
    mediciones: [],
    medicionesSubida: requiereHisteresis && histeresisEnTodos ? [] : undefined,
    medicionesBajada: requiereHisteresis && histeresisEnTodos ? [] : undefined,
    puntoHisteresis: undefined
  };

  // Las mediciones están organizadas secuencialmente por patrón y punto
  // Cada patrón tiene cantPuntos mediciones (+ 1 si hay histéresis adicional)
  const cantMedicionesPorPatron = cantPuntos + 
    (requiereHisteresis && !histeresisEnTodos ? 1 : 0);
  
  const inicio = patronIdx * cantMedicionesPorPatron;
  const fin = inicio + cantMedicionesPorPatron;
  const medicionesDeEstePatron = medicionesPatrones.slice(inicio, fin);

  medicionesDeEstePatron.forEach((medicion, idx) => {
    if (idx < cantPuntos) {
      estructura.puntoCalibracion[idx] = medicion.puntoCal || '';
      
      if (requiereHisteresis && histeresisEnTodos) {
        estructura.medicionesSubida[idx] = medicion.medicionesSubida || Array(cantMediciones).fill('');
        estructura.medicionesBajada[idx] = medicion.medicionesBajada || Array(cantMediciones).fill('');
      } else {
        estructura.mediciones[idx] = medicion.mediciones || Array(cantMediciones).fill('');
      }
    } else {
      // Es el punto de histéresis adicional
      if (requiereHisteresis && !histeresisEnTodos) {
        estructura.puntoHisteresis = {
          puntoCal: medicion.puntoCal || '',
          mediciones: medicion.mediciones || Array(cantMediciones).fill('')
        };
      }
    }
  });

  // Rellenar con valores vacíos si faltan puntos
  while (estructura.puntoCalibracion.length < cantPuntos) {
    estructura.puntoCalibracion.push('');
    if (estructura.mediciones) {
      estructura.mediciones.push(Array(cantMediciones).fill(''));
    }
    if (estructura.medicionesSubida) {
      estructura.medicionesSubida.push(Array(cantMediciones).fill(''));
      estructura.medicionesBajada.push(Array(cantMediciones).fill(''));
    }
  }

  return estructura;
};

/**
 * Convierte medicionesIBC del esquema DB al formato UI
 * @param {Array} medicionesIBC - Array según medicionIBCSchema
 * @param {Object} magnitudConfig - Configuración de la magnitud
 * @returns {Object} Estructura para UI
 */
export const convertirEsquemaAIBC = (medicionesIBC, magnitudConfig) => {
  // Normalizar nombres por si vienen del esquema DB o del useEffect
  const cantPuntos = magnitudConfig.cantidadPuntos ?? magnitudConfig.cantPuntos;
  const requiereHisteresis = magnitudConfig.histeresis ?? magnitudConfig.requiereHisteresis;
  const histeresisEnTodos = magnitudConfig.histeresisEnTodosLosPuntos;
  const cantMediciones = magnitudConfig.cantMedicionesPorPunto;

  if (!medicionesIBC || medicionesIBC.length === 0) {
    return {
      puntoCalibracion: Array(cantPuntos).fill(''),
      mediciones: Array(cantPuntos).fill(null).map(() => Array(cantMediciones).fill('')),
      puntoBajadaCalibracion: requiereHisteresis && histeresisEnTodos ? Array(cantPuntos).fill('') : null,
      medicionesSubida: requiereHisteresis && histeresisEnTodos
        ? Array(cantPuntos).fill(null).map(() => Array(cantMediciones).fill('')) : null,
      medicionesBajada: requiereHisteresis && histeresisEnTodos
        ? Array(cantPuntos).fill(null).map(() => Array(cantMediciones).fill('')) : null,
      puntoHisteresis: requiereHisteresis && !histeresisEnTodos
        ? { puntoCal: '', mediciones: Array(cantMediciones).fill('') } : null
    };
  }

  const estructura = {
    puntoCalibracion: [],
    mediciones: [],
    puntoBajadaCalibracion: requiereHisteresis && histeresisEnTodos ? [] : null,
    medicionesSubida: requiereHisteresis && histeresisEnTodos ? [] : null,
    medicionesBajada: requiereHisteresis && histeresisEnTodos ? [] : null,
    puntoHisteresis: null
  };

  medicionesIBC.forEach((medicion, idx) => {
    if (idx < cantPuntos) {
      estructura.puntoCalibracion[idx] = medicion.puntoCal || '';

      if (requiereHisteresis && histeresisEnTodos) {
        estructura.medicionesSubida[idx] = medicion.medicionesSubida || Array(cantMediciones).fill('');
        estructura.medicionesBajada[idx] = medicion.medicionesBajada || Array(cantMediciones).fill('');
      } else {
        estructura.mediciones[idx] = medicion.mediciones || Array(cantMediciones).fill('');
      }
    } else {
      if (requiereHisteresis && !histeresisEnTodos) {
        estructura.puntoHisteresis = {
          puntoCal: medicion.puntoCal || '',
          mediciones: medicion.mediciones || Array(cantMediciones).fill('')
        };
      }
    }
  });

  // Rellenar puntos faltantes
  while (estructura.puntoCalibracion.length < cantPuntos) {
    estructura.puntoCalibracion.push('');
    estructura.mediciones.push(Array(cantMediciones).fill(''));
    if (estructura.medicionesSubida) {
      estructura.medicionesSubida.push(Array(cantMediciones).fill(''));
      estructura.medicionesBajada.push(Array(cantMediciones).fill(''));
    }
  }

  return estructura;
};

/**
 * Convierte datos de estabilidad del formato UI al esquema de DB
 * @param {Array} puntosEstabilidad - Array con mediciones por punto
 * @param {Array} puntosCalibracion - Valores de los puntos de calibración
 * @param {Object} config - { patronUsado, criterio }
 * @param {Object} magnitudConfig - Configuración de la magnitud
 * @returns {Array} estabilidad según esquema DB
 */
export const convertirEstabilidadAEsquema = (puntosEstabilidad, puntosCalibracion, config, magnitudConfig) => {
  if (!puntosEstabilidad || puntosEstabilidad.length === 0) return [];

  // Criterio por punto: `config.criterios[]` es la fuente moderna; `config.criterio`
  // (single) es fallback para compatibilidad con datos/UI antiguos.
  const criteriosArr = Array.isArray(config.criterios) ? config.criterios : [];
  const criterioFallback = config.criterio ?? null;

  return puntosEstabilidad.map((mediciones, puntoIdx) => {
    const criterioDelPunto = (criteriosArr[puntoIdx] !== undefined && criteriosArr[puntoIdx] !== '')
      ? Number(criteriosArr[puntoIdx])
      : (criterioFallback !== null && criterioFallback !== '' ? Number(criterioFallback) : null);

    const validos = mediciones.filter(m => m !== '' && !isNaN(m));
    let cumple = null;
    if (validos.length >= 2 && criterioDelPunto !== null && !Number.isNaN(criterioDelPunto)) {
      const max = Math.max(...validos);
      const min = Math.min(...validos);
      cumple = (max - min) <= criterioDelPunto;
    }

    return {
      patron: config.patronUsado || null,
      criterio: criterioDelPunto,
      puntoCal: puntosCalibracion[puntoIdx] || 0,
      // Compat de nombres: plantilla usa `cantPuntos`, lote usa `cantidadPuntos`.
      cantidadPuntos: magnitudConfig.cantidadPuntos ?? magnitudConfig.cantPuntos,
      mediciones: mediciones,
      cumple
    };
  });
};

/**
 * Convierte estabilidad del esquema DB al formato UI
 * @param {Array} estabilidadDB - Array según estabilidadSchema
 * @param {Object} magnitudConfig - Configuración de la magnitud
 * @returns {Object} { puntos: [], patronUsado, criterio }
 */
export const convertirEsquemaAEstabilidad = (estabilidadDB, magnitudConfig) => {
  // Compat de nombres: `PlantillaProcedimiento.magnitudesConfig[i]` usa `cantPuntos`,
  // pero `LoteCalibracion.sesiones[i]` usa `cantidadPuntos`. Este helper se llama
  // con AMBOS shapes según el caller — aceptar los dos evita que `cantPuntos`
  // termine `undefined` (con `Array(undefined) = []` reseteando todos los criterios).
  const cantPuntos = magnitudConfig.cantidadPuntos ?? magnitudConfig.cantPuntos ?? 0;
  const cantMed = magnitudConfig.cantMedicionesEstabilidad || 10;

  if (!estabilidadDB || estabilidadDB.length === 0) {
    return {
      puntos: Array(cantPuntos).fill(null).map(() => Array(cantMed).fill('')),
      patronUsado: '',
      criterios: Array(cantPuntos).fill(''),
      // `criterio` (single) queda por compat con consumidores viejos.
      criterio: ''
    };
  }

  // Longitud efectiva: max(cantPuntos, estabilidadDB.length) — si por algún
  // motivo la DB trae MÁS entradas que las definidas en la plantilla, no las
  // descartamos silenciosamente.
  const N = Math.max(cantPuntos, estabilidadDB.length);

  // IMPORTANTE: `puntos` DEBE tener length N — si queda más corto,
  // `convertirEstabilidadAEsquema` iteraría sobre menos entries y perdería
  // los criterios/mediciones de los índices altos.
  const puntos = Array(N).fill(null).map((_, i) => (
    estabilidadDB[i]?.mediciones || []
  ));
  const config = estabilidadDB[0] || {};
  const criterios = Array(N).fill('').map((_, i) => {
    const v = estabilidadDB[i]?.criterio;
    return v !== undefined && v !== null ? v : '';
  });

  return {
    puntos,
    patronUsado: config.patron?._id || config.patron || '',
    criterios,
    criterio: config.criterio || '' // compat
  };
};
